import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { BreadcrumbItem, Breadcrumbs, Tab, Tabs, Spacer, Badge, Button, Spinner } from '@nextui-org/react'
import api from 'src/lib/api'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import AccountSetting from './account-setting'
import BillingSetting from './billing-setting'
import CompanyDetails from 'src/pages/settings/company-details'
import { useWorkspaceContextData } from 'src/context/workspace'
import PowerdocsCustomize from 'src/pages/settings/powerdocs-customize'
import { Icon } from '@iconify/react'
import CompanyInfo from '../admin/components/company-info'
import CompanyLegal from '../admin/components/company-legal'
import CompanyFinancial from '../admin/components/company-financial'
import CompanyHR from '../admin/components/company-hr'
import CompanyTechnicalCapabilities from '../admin/components/company-technical'
import CompanyReferences from '../admin/components/company-references'
import CompanyTenderExperience from '../admin/components/company-tenders-experience'

export default function CompanyProfile() {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [profile, setPorfile] = useState<any>({})
    const loadUser = () => {
        const userString = localStorage.getItem('user')
        if (userString) {
            let u = JSON.parse(userString)
            setPorfile(u?.companyProfile ?? {})
        }
        api
            .get('/agencies/profile')
            .then((res) => {
                localStorage.setItem('user', JSON.stringify(res.data))
                setPorfile(res.data?.companyProfile)
            })
            .catch((e) => {
                console.log('Error loading current profile')
            })
    }
    const updateCompanyProfile = async () => {
        setLoading(true)
        try {
            await api.post('/agencies/company-profile', profile)
        } catch (e) {
            console.log('Error updating company profile')
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        loadUser()
    }, [])

    const { tab } = useParams()
    const [selected, setSelected] = React.useState(tab)
    const [selectedCompanyKey, setSelectedCompanyKey] = React.useState('general-info')
    const [workspace, setWorkspace] = useState(null)
    const { activeWorkspace } = useWorkspaceContextData()
    useEffect(() => {
        if (profile && activeWorkspace) {
            const ws = (profile.workspaces || []).filter((w: { uuid: string }) => w.uuid === activeWorkspace).pop()
            if (ws) setWorkspace(ws)
        }
    }, [profile, activeWorkspace])
    const navigate = useNavigate()
    const tabs = {
        profile: 'Profile',
        company: 'Company details',
        powerdocs: 'Personalize Powerdocs AI',
        billing: 'Billing details',
    }

    const handleChange = (key: string, value: any) => {
        setPorfile((prev: any) => ({ ...prev, [key]: value }))
    }
    return (
        <>
            <Helmet>
                <title>{t('title')}</title>
            </Helmet>
            <div className="flex flex-col w-full h-screen p-3 overflow-y-auto">

                <Breadcrumbs className={'pl-2 pt-4'}>
                    <BreadcrumbItem onClick={() => navigate('/')}>
                        <Icon icon={'lucide:home'} /> Home
                    </BreadcrumbItem>
                    <BreadcrumbItem onClick={() => navigate('/settings')}>Settings</BreadcrumbItem>
                    <BreadcrumbItem onClick={() => navigate('/settings/company-profile')}>Company Profile</BreadcrumbItem>
                </Breadcrumbs>

                {/*  Tabs */}
                <div className="flex flex-col gap-3 w-full px-2 2xl:px-8 max-w-[80vw]">
                    <Tabs
                        fullWidth
                        aria-label="Settings"
                        selectedKey={selected}
                        onSelectionChange={(key: string | number) => {
                            setSelected(key as string)
                        }}
                        classNames={{
                            base: 'mt-6',
                            cursor: 'bg-content1 dark:bg-content1',
                            panel: 'w-full p-0 pt-1',
                        }}
                    >
                        <Tab key="general-info" title={
                            <>
                                <span>General Information</span>
                                {
                                    !(profile?.legalName || profile?.legalForm || profile?.registrationNumber || profile?.yearOfEstablishment || profile?.companySize || profile?.officeLocation || profile?.primaryContact) &&
                                    <div className="relative inline-flex ml-1">
                                        <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                        <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                    </div>
                                }
                            </>
                        }>
                            <CompanyInfo formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                        <Tab key="tender-experience" title={
                            <>
                                <span>Tender Experience</span>
                                {
                                    !(profile?.experienceWithTendersPlatform || profile?.prequalificationText || profile?.jointVenture)
                                    &&
                                    (
                                        <div className="relative inline-flex ml-1">
                                            <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                            <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                        </div>
                                    )
                                }
                            </>
                        }>
                            <CompanyTenderExperience formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                        <Tab key="legal-info" title={
                            <>
                                <span>Legal & Compliance</span>
                                {
                                    !(
                                        profile?.commercialRegister?.length > 0 ||
                                        profile?.tradeCertificate?.length > 0 ||
                                        profile?.authorizedSignatoriesFiles?.length > 0 ||
                                        profile?.taxComplianceCertificate?.length > 0 ||
                                        profile?.criminalRecordAttachments?.length > 0 ||
                                        profile?.complianceCertificateFiles?.length > 0 ||
                                        profile?.antiCorruptionFiles?.length > 0 ||
                                        profile?.authorizedSignatories?.length > 0 ||
                                        profile?.taxCompliance?.length > 0 ||
                                        profile?.criminalRecordDescription?.length > 0 ||
                                        profile?.complianceCertificateText?.length > 0 ||
                                        profile?.gdprMeasures?.length > 0 ||
                                        profile?.antiCorruptionText?.length > 0
                                    )
                                    &&
                                    (
                                        <div className="relative inline-flex ml-1">
                                        <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                        <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                        </div>       
                                    )
                                }
                            </>
                        }>
                            <CompanyLegal formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                        <Tab key="hr" title={
                            <>
                                <span>Human Resources</span>
                                {
                                    !(profile?.keyPersonnel?.length > 0 || profile?.projectManager?.length > 0 || profile?.staffCV?.length > 0 || profile?.languageCapabilities?.length > 0 || profile?.trainingPrograms?.length > 0)
                                    &&
                                    (
                                        <div className="relative inline-flex ml-1">
                                            <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                            <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                        </div>
                                    )
                                }
                            </>
                        }>
                            <CompanyHR formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                        <Tab key="technical" title={
                            <>
                                <span>Technical Capabilities</span>
                                {
                                    !(profile?.coreServiceArea?.length > 0 || profile?.programmingLanguages?.length > 0 || profile?.toolsPractices?.length > 0 || profile?.cloudInfrastructure?.length > 0)
                                    &&
                                    (
                                        <div className="relative inline-flex ml-1">
                                            <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                            <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                        </div>
                                    )
                                }
                            </>
                        }>
                            <CompanyTechnicalCapabilities formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                        <Tab key="references" title={
                            <>
                                <span>References</span>
                                {
                                    !(profile?.referenceProjectsText?.length > 0 || profile?.referenceContactPerson?.length > 0 || profile?.hasPublicSectorReferences || profile?.publicSectorReferences?.length > 0 || profile?.caseStudyDocumentation?.length > 0 || profile?.awards?.length > 0)
                                    &&
                                    (
                                        <div className="relative inline-flex ml-1">
                                            <div className="rounded-full bg-warning-400 h-[8px] w-[8px] inline-block mr-2"></div>
                                            <div className="absolute animate-ping rounded-full bg-warning-400 h-[8px] w-[8px] mr-2"></div>
                                        </div>
                                    )
                                }
                            </>
                        }>
                            <CompanyReferences formData={profile} handleChange={handleChange} isForUser />
                        </Tab>
                    </Tabs>
                </div>
                <Spacer y={2} />
                <div className='flex flex-row justify-end'>
                    <Button className="mt-5" color="secondary" size="md" radius="md" onClick={updateCompanyProfile} isDisabled={loading}>
                        {loading ? (
                            <span className={'flex flex-row justify-center items-center gap-2'}>
                                Updating information <Spinner size={'sm'} color={'white'} />
                            </span>
                        ) : (
                            'Update Details'
                        )}{' '}
                    </Button>
                </div>
            </div>
        </>
    )
}
